import { ReactComponent as EngagementIcon } from "shared/assets/images/icon-engagement.svg";
import { ReactComponent as FollowersIcon } from "shared/assets/images/icon-followers.svg";
import { ReactComponent as MusicIcon } from "shared/assets/images/icon-music.svg";
import { ReactComponent as PerformanceIcon } from "shared/assets/images/icon-performance.svg";
import { useSelector } from "react-redux";
import { ArtistState } from "explorer/reducers/artistas";
import { Artist } from "model/Artist";
import { useState, useRef, useEffect } from "react";
import ProfileProgressRing from "./ProfileProgressRing";
import CareerLevelBadge from "explorer/pages/home/CareerLevelBadge";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { motion } from "motion/react";
import "./ArtistMetric.scss";
import { getI18nGenre } from "shared/helpers/i18nGenre";

const getMetrics = (userArtist: Artist) => [
  {
    label: "pages.Home.engagement",
    value: userArtist?.engagementDif ?? 0,
    icon: <EngagementIcon stroke="#CECDFF" />,
    hasDiff: true,
  },
  {
    label: "pages.Home.followers",
    value: userArtist?.followersDif ?? 0,
    icon: <FollowersIcon stroke="#CECDFF" />,
    hasDiff: true,
  },
  {
    label: "pages.Home.music",
    value: userArtist?.developmentPercentual ?? 0,
    icon: <MusicIcon fill="#CECDFF" />,
    hasDiff: false,
  },
  {
    label: "pages.Home.performance",
    value: userArtist?.performancePercentual ?? 0,
    icon: <PerformanceIcon fill="#CECDFF" />,
    hasDiff: false,
  },
];

const ArtistMetrics: React.FC = () => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const metricsRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const { genres } = useSelector((state: any) => state.genres);
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const metrics = getMetrics(userArtist);
  const level = userArtist.stage || 0;

  let { titulo } = t(`pages.EstagioArtista.estagio.${level || 0}`) as any;

  const formatNumber = (value: number) => {
    if (Math.abs(value) > 0 && Math.abs(value) < 1) {
      return Math.abs(value).toFixed(2);
    }
    return Math.abs(Math.round(value)).toString();
  };

  useEffect(() => {
    if (!showDetails) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        metricsRef.current &&
        !metricsRef.current.contains(event.target as Node)
      ) {
        setShowDetails(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDetails]);

  return (
    <div className="artist-metrics" ref={metricsRef}>
      <div
        className={`artist-metrics__row ${isMobile ? "mobile" : ""}`}
        onClick={() => setShowDetails(!showDetails)}
      >
        {metrics.map((metric) => {
          return (
            <div key={metric.label} className="artist-metrics__item">
              <div className="artist-metrics__item-content">
                <span className="artist-metrics__icon">{metric.icon}</span>
                <span className="artist-metrics__value body-small">
                  {formatNumber(metric.value)}%
                </span>
                {metric.hasDiff &&
                  metric.value !== 0 &&
                  (metric.value > 0 ? (
                    <span className="artist-metrics__up">↑</span>
                  ) : (
                    <span className="artist-metrics__down">↓</span>
                  ))}
              </div>
            </div>
          );
        })}
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, y: -50, x: isMobile ? 0 : "-50%" }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: "easeInOut" }}
            className="artist-metrics__details"
          >
            <div className="artist-card">
              <div className="artist-card__progress">
                <ProfileProgressRing size={isMobile ? 50 : 70} />
              </div>
              <div className="artist-card__info">
                <div className="artist-card__name heading-h5">
                  {userArtist?.name}
                </div>
                <div className="artist-card__genre body-small">
                  {userArtist?.genresStrm?.length &&
                    getI18nGenre(userArtist?.genresStrm?.[0]!, i18n, genres)}
                </div>
              </div>
              <div className="artist-card__level">
                <div className="artist-card__level-label body-small">
                  {t("pages.Home.artistLevel")}
                </div>
                <div className="artist-card__level-value body-large">
                  {titulo}
                </div>
              </div>
            </div>
            <div className="artist-metrics__details-row colors">
              <div
                className="artist-metrics__item"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: 50,
                  flex: 0,
                }}
              >
                <span
                  className="body-small"
                  style={{
                    textAlign: "center",
                    width: "100%",
                    color: "#cecdff",
                  }}
                >
                  Level
                </span>
                <CareerLevelBadge
                  level={level}
                  width={40}
                  height={46}
                  showTitle={false}
                />
              </div>

              {metrics.map((m) => (
                <div key={m.label} className="artist-metrics__item body-small">
                  <span
                    className=""
                    style={{ width: "100%", color: "#cecdff" }}
                  >
                    {t(m.label)}
                  </span>
                  <div className="artist-metrics__item-content ">
                    <span className="artist-metrics__icon">{m.icon}</span>
                    <span className="artist-metrics__value">
                      {formatNumber(m.value)}%
                    </span>
                    {m.hasDiff &&
                      m.value !== 0 &&
                      (m.value > 0 ? (
                        <span className="artist-metrics__up">↑</span>
                      ) : (
                        <span className="artist-metrics__down">↓</span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ArtistMetrics;
